/**
 * ConversationMediaTab
 * Galeria de mídias organizada por tipo: Fotos, Vídeos, Documentos, Áudios
 * Permite seleção e download
 */

import React, { useState } from 'react';
import { toast } from 'sonner';
import type { ConversationResponse, ConversationMediaItem } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { useConversationMedia } from '../../hooks/useConversationMedia';
import { pauseOtherMedia } from '../../utils/mediaPlayback';
import { downloadMediaWithFilename } from '../../utils/downloadMedia';

interface ConversationMediaTabProps {
  conversation: ConversationResponse;
}

type MediaType = 'image' | 'video' | 'document' | 'audio';
type SubTab = MediaType;

const subTabLabels: Record<SubTab, string> = {
  image: 'Fotos',
  video: 'Vídeos',
  document: 'Documentos',
  audio: 'Áudios',
};

type FileItem = ConversationMediaItem;

function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('pt-BR');
  } catch {
    return dateStr;
  }
}

export const ConversationMediaTab: React.FC<ConversationMediaTabProps> = ({ conversation }) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('image');
  const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set());

  // Mídia paginada via endpoint dedicado (antes vinha em conversation.files,
  // agregado inteiro num documento — estourava 16MB do MongoDB).
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useConversationMedia(conversation.id, activeSubTab);

  const currentFiles: FileItem[] = data?.pages.flatMap((p) => p.items) ?? [];

  // Contagens por tipo — globais (idênticas em toda página), usadas nos badges.
  const counts = data?.pages[0]?.counts ?? { total: 0, image: 0, video: 0, audio: 0, document: 0 };

  const toggleSelect = (url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedUrls(new Set(currentFiles.map((f) => f.url)));
  };

  const clearSelection = () => {
    setSelectedUrls(new Set());
  };

  const downloadSelected = async () => {
    const files = currentFiles.filter((f) => selectedUrls.has(f.url));
    let hadError = false;
    for (const file of files) {
      try {
        // eslint-disable-next-line no-await-in-loop -- downloads sequenciais evitam popup-block/objectURL colidindo
        await downloadMediaWithFilename(file.url, file.filename || file.url.split('/').pop() || 'arquivo');
      } catch {
        hadError = true;
      }
    }
    if (hadError) {
      toast.error('Falha ao baixar um ou mais arquivos');
    }
  };

  const subTabs: SubTab[] = ['image', 'video', 'document', 'audio'];

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tabs pill bar */}
      <div className="flex gap-2 px-4 pt-3 pb-2 flex-shrink-0">
        {subTabs.map((tab) => {
          const count = counts[tab];
          const isActive = tab === activeSubTab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => { setActiveSubTab(tab); clearSelection(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
              style={{
                backgroundColor: isActive ? '#8b5cf6' : '#f1f5f9',
                color: isActive ? '#ffffff' : '#64748b',
              }}
            >
              {subTabLabels[tab]}
              {count > 0 && (
                <span
                  className="px-1.5 py-0.5 rounded-full text-xs"
                  style={{
                    backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(139,92,246,0.15)',
                    color: isActive ? '#ffffff' : '#8b5cf6',
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selection toolbar */}
      {currentFiles.length > 0 && (
        <div
          className="flex items-center justify-between px-4 py-1.5 flex-shrink-0 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={selectedUrls.size === currentFiles.length ? clearSelection : selectAll}
              className="text-xs font-medium"
              style={{ color: '#8b5cf6' }}
            >
              {selectedUrls.size === currentFiles.length ? 'Desmarcar todos' : 'Selecionar todos'}
            </button>
            {selectedUrls.size > 0 && (
              <span className="text-xs" style={{ color: '#94a3b8' }}>
                {selectedUrls.size} selecionado{selectedUrls.size !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {selectedUrls.size > 0 && (
            <button
              type="button"
              onClick={() => void downloadSelected()}
              className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors"
              style={{ backgroundColor: '#8b5cf6', color: '#ffffff' }}
              data-testid="conversation-media-download-selected"
            >
              <Icon name="download" className="w-3.5 h-3.5" />
              Baixar
            </button>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Icon name="loader" className="w-6 h-6 animate-spin" style={{ color: '#8b5cf6' }} />
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              Carregando...
            </p>
          </div>
        ) : currentFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#f1f5f9' }}
            >
              <Icon name="image" className="w-6 h-6" style={{ color: '#94a3b8' }} />
            </div>
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              Nenhum arquivo deste tipo
            </p>
          </div>
        ) : activeSubTab === 'image' ? (
          /* Photo grid — 4 cols */
          <div className="grid grid-cols-4 gap-2">
            {currentFiles.map((file, i) => {
              const isSelected = selectedUrls.has(file.url);
              return (
                <div
                  key={i}
                  className="relative aspect-square rounded-lg overflow-hidden cursor-pointer"
                  style={{
                    border: isSelected ? '2px solid #8b5cf6' : '2px solid transparent',
                  }}
                  onClick={() => toggleSelect(file.url)}
                >
                  <img
                    src={file.thumbnailUrl ?? file.url}
                    alt={file.caption ?? file.filename ?? 'imagem'}
                    className="w-full h-full object-cover"
                  />
                  {isSelected && (
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(139,92,246,0.3)' }}
                    >
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: '#8b5cf6' }}
                      >
                        <Icon name="check" className="w-4 h-4" style={{ color: '#ffffff' }} />
                      </div>
                    </div>
                  )}
                  <div
                    className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-xs truncate"
                    style={{
                      backgroundColor: 'rgba(0,0,0,0.5)',
                      color: '#ffffff',
                    }}
                  >
                    {formatDate(file.sentAt)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : activeSubTab === 'video' ? (
          /* Video grid with play icon */
          <div className="grid grid-cols-3 gap-2">
            {currentFiles.map((file, i) => {
              const isSelected = selectedUrls.has(file.url);
              return (
                <div
                  key={i}
                  className="relative aspect-video rounded-lg overflow-hidden cursor-pointer"
                  style={{
                    backgroundColor: '#0f172a',
                    border: isSelected ? '2px solid #8b5cf6' : '2px solid transparent',
                  }}
                  onClick={() => toggleSelect(file.url)}
                >
                  {file.thumbnailUrl ? (
                    <img
                      src={file.thumbnailUrl}
                      alt={file.caption ?? 'vídeo'}
                      className="w-full h-full object-cover opacity-80"
                    />
                  ) : (
                    <div className="w-full h-full" style={{ backgroundColor: '#1e293b' }} />
                  )}
                  {/* Play icon overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                    >
                      <Icon name="play" className="w-5 h-5" style={{ color: '#ffffff' }} />
                    </div>
                  </div>
                  {isSelected && (
                    <div
                      className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: '#8b5cf6' }}
                    >
                      <Icon name="check" className="w-4 h-4" style={{ color: '#ffffff' }} />
                    </div>
                  )}
                  <div
                    className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-xs truncate"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: '#ffffff' }}
                  >
                    {file.caption ?? file.filename ?? formatDate(file.sentAt)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : activeSubTab === 'audio' ? (
          /* Audio list */
          <div className="space-y-2">
            {currentFiles.map((file, i) => {
              const isSelected = selectedUrls.has(file.url);
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                  style={{
                    backgroundColor: isSelected ? 'rgba(139,92,246,0.08)' : '#f8fafc',
                    border: `1px solid ${isSelected ? '#8b5cf6' : '#e5e7eb'}`,
                  }}
                  onClick={() => toggleSelect(file.url)}
                >
                  <div
                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(139,92,246,0.12)' }}
                  >
                    <Icon name="mic" className="w-4 h-4" style={{ color: '#8b5cf6' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <audio
                      src={file.url}
                      controls
                      className="w-full h-8"
                      style={{ maxWidth: '100%' }}
                      onClick={(e) => e.stopPropagation()}
                      onPlay={(e) => pauseOtherMedia(e.currentTarget)}
                    />
                  </div>
                  <span className="text-xs flex-shrink-0" style={{ color: '#94a3b8' }}>
                    {formatDate(file.sentAt)}
                  </span>
                  {isSelected && (
                    <div
                      className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: '#8b5cf6' }}
                    >
                      <Icon name="check" className="w-3.5 h-3.5" style={{ color: '#ffffff' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Document list */
          <div className="space-y-2">
            {currentFiles.map((file, i) => {
              const isSelected = selectedUrls.has(file.url);
              return (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors"
                  style={{
                    backgroundColor: isSelected ? 'rgba(139,92,246,0.08)' : '#f8fafc',
                    border: `1px solid ${isSelected ? '#8b5cf6' : '#e5e7eb'}`,
                  }}
                  onClick={() => toggleSelect(file.url)}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(139,92,246,0.12)' }}
                  >
                    <Icon name="file-text" className="w-5 h-5" style={{ color: '#8b5cf6' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                      {file.filename ?? file.caption ?? 'Documento'}
                    </p>
                    <p className="text-xs" style={{ color: '#94a3b8' }}>
                      {formatFileSize(file.size)}{file.size ? ' · ' : ''}{formatDate(file.sentAt)}
                    </p>
                  </div>
                  {isSelected && (
                    <div
                      className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: '#8b5cf6' }}
                    >
                      <Icon name="check" className="w-3.5 h-3.5" style={{ color: '#ffffff' }} />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void (async () => {
                        try {
                          await downloadMediaWithFilename(file.url, file.filename || file.url.split('/').pop() || 'arquivo');
                        } catch {
                          toast.error('Falha ao baixar o arquivo');
                        }
                      })();
                    }}
                    className="flex-shrink-0 p-1.5 rounded-lg transition-colors border-0 bg-transparent cursor-pointer"
                    style={{ color: '#64748b' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    aria-label="Baixar"
                    data-testid={`conversation-media-document-download-${i}`}
                  >
                    <Icon name="download" className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Carregar mais — paginação da galeria */}
        {!isLoading && hasNextPage && (
          <div className="flex justify-center pt-4">
            <button
              type="button"
              onClick={() => void fetchNextPage()}
              disabled={isFetchingNextPage}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60"
              style={{ backgroundColor: '#f1f5f9', color: '#8b5cf6' }}
            >
              {isFetchingNextPage ? (
                <Icon name="loader" className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Icon name="chevron-down" className="w-3.5 h-3.5" />
              )}
              {isFetchingNextPage ? 'Carregando...' : 'Carregar mais'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationMediaTab;
